import { useState } from 'react';
import { ParameterList } from 'src/components/molecules';
import { RoutingRuleType } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag } from 'src/types';
import type { RoutingVariantType } from '../../RoutingRuleAndPriority';
import { readRoutingRule, RoutingRule } from '../helpers';

interface Parameters {
  rule: RoutingRuleType;
  variant?: RoutingVariantType;
}

export const useRoutingRuleParameters = ({ rule, variant }: Parameters) => {
  const [parameterList, setParameterList] = useState<ParameterList>(
    readRoutingRule(rule.rule as { all: RoutingRule[] }),
  );

  const oprEnabled = useIsFeatureEnabled(FeatureFlag.PUBLIC_REPORTING);
  const vaultTalkEnabled = useIsFeatureEnabled(FeatureFlag.VAULT_TALK);
  const routingCompanyRelationship = useIsFeatureEnabled(
    FeatureFlag.ROUTING_COMPANY_RELATIONSHIP,
  );
  const relationshipToOrgEnabled = useIsFeatureEnabled(
    FeatureFlag.RELATIONSHIP_TO_ORG,
  );

  const coiParameterOptions = [
    { value: 'reportedCategory', label: 'COI Category' },
    { value: 'questionnaireId', label: 'COI Form' },
    { value: 'site', label: 'Site' },
    { value: 'department', label: 'Department' },
    { value: 'country', label: 'Country' },
    { value: 'systemRoles', label: 'Permissions' },
  ];

  const speakUpParameterOptions = [
    { value: 'site', label: 'Site' },
    { value: 'department', label: 'Department' },
    { value: 'country', label: 'Country' },
    { value: 'anonymous', label: 'Anonymous' },
    { value: 'reportedCategory', label: 'Reported Category' },
    { value: 'systemRoles', label: 'Permissions' },
    { value: 'submissionMode', label: 'Intake Method' },
  ];

  const parameterOptions =
    variant === 'coi' ? coiParameterOptions : speakUpParameterOptions;

  if (
    oprEnabled ||
    vaultTalkEnabled ||
    routingCompanyRelationship ||
    relationshipToOrgEnabled
  ) {
    parameterOptions.push({
      value: 'relationshipToCompany',
      label: 'Relationship to Organization',
    });
  }
  if (routingCompanyRelationship && variant === 'speak-up') {
    parameterOptions.push({
      value: 'userMetaData.organization',
      label: 'Business Unit',
    });
    parameterOptions.push({
      value: 'userMetaData.division',
      label: 'Business Department',
    });
  }
  if (vaultTalkEnabled && variant === 'speak-up') {
    parameterOptions.push({
      value: 'Call.organisationHotlineNumber',
      label: 'VaultTalk Phone number',
    });
  }

  return { parameterList, setParameterList, parameterOptions };
};
